// A part's SKU box: its type's prefix (MAT-, PAK-, SUB-, SVC-: shared/part-sku.ts) sits fixed in front of the text, so
// select-all and typing replace only the rest, the prefix can't be cleared by accident, and a pasted whole SKU loses
// its prefix rather than doubling it. Every part SKU editor uses it: the item panel (RecordPanel's `prefix`), New part
// (CreateDialog), New packaging part, Add a part like this and a part group's SKU prefix.
import type { ComponentProps } from "react";
import { skuStem } from "../../../shared/part-sku.ts";
import { useCaret } from "./caret.ts";
import { cx } from "./controls.tsx";

/** The text after the prefix, as typed or pasted: upper-case, without a type prefix of its own. */
export const skuRest = (text: string) => skuStem(text.toUpperCase());

/** A SKU's text after `prefix` (one from before the prefixes, or with another type's, is all text). */
export const restOf = (sku: string, prefix: string) => (prefix && sku.toUpperCase().startsWith(prefix) ? sku.slice(prefix.length) : skuStem(sku));

export function SkuInput({
  prefix,
  value,
  onChange,
  className,
  ref,
  ...props
}: { prefix: string; value: string; onChange: (rest: string) => void } & Omit<ComponentProps<"input">, "value" | "onChange">) {
  // Typing in the middle keeps the cursor there, though the text is upper-cased as it's typed (caret.ts).
  const caret = useCaret(ref);
  return (
    <div
      className={cx(
        "flex h-8 w-full items-center rounded-md border border-line bg-surface font-mono text-[12px] hover:border-line-strong focus-within:border-accent",
        className,
      )}
    >
      {prefix && (
        <span
          className="cursor-text pl-2.5 text-muted select-none"
          title={`Every ${prefix} part's SKU starts with ${prefix}: it follows the part's type`}
          onMouseDown={(e) => {
            e.preventDefault();
            (e.currentTarget.nextElementSibling as HTMLInputElement | null)?.focus();
          }}
        >
          {prefix}
        </span>
      )}
      <input
        spellCheck={false}
        autoComplete="off"
        {...props}
        ref={caret.ref}
        value={value}
        onChange={(e) => onChange(caret.rewrite(e, prefix ? skuRest : (s) => s.toUpperCase()))}
        className={cx("h-full min-w-0 flex-1 bg-transparent pr-2.5 placeholder:text-faint focus:outline-none", prefix ? "pl-0" : "pl-2.5")}
      />
    </div>
  );
}
